import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  type AppCheck,
} from 'firebase/app-check';
import { getAI, VertexAIBackend } from 'firebase/ai';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const firebaseApp = initializeApp(firebaseConfig);

// --- App Check (reCAPTCHA Enterprise) -----------------------------------
// Must run before any Firebase service that App Check protects. We only
// attempt this in the browser and never when pointing at the emulator —
// the emulator doesn't enforce App Check and the token request would be
// wasted noise. If no reCAPTCHA key is configured we skip gracefully so
// local dev keeps working without any Firebase console setup.
const usingEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
const appCheckDebug =
  usingEmulator || import.meta.env.VITE_APP_CHECK_DEBUG === 'true';

// Module-local handle used by `auth-headers.ts` to mint the
// `X-Firebase-AppCheck` header on `/api/*` fetches. Null when the key is
// missing (local dev, unset env) or init throws.
let appCheckInstance: AppCheck | null = null;

export function getAppCheckInstance(): AppCheck | null {
  return appCheckInstance;
}

if (typeof window !== 'undefined' && !usingEmulator) {
  const recaptchaKey = import.meta.env.VITE_APP_CHECK_RECAPTCHA_KEY;
  // Silently skip when no key is provisioned. App Check only engages when
  // the key is present; enabling it in prod is a deployment decision (see
  // the platform docs), not something the console needs to remind us of.
  if (recaptchaKey) {
    try {
      if (appCheckDebug) {
        // The SDK reads this global flag at init time. Must be set BEFORE
        // calling initializeAppCheck.
        (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean })
          .FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
      appCheckInstance = initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaEnterpriseProvider(recaptchaKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (err) {
      // Rare: initializeAppCheck throws on malformed providers or a key
      // that doesn't match the origin. Surfacing it via console.warn keeps
      // the error visible without pulling in `logger` (which imports this
      // module and would create a cycle).
      console.warn('[firebase] App Check init failed:', err);
    }
  }
}

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

// Firebase AI Logic — VertexAIBackend routes through Vertex AI (project's
// runtime, not the public Gemini Developer API). Stronger no-training
// data-handling defaults — important for the sovereign-intelligence brief.
// Used for latency-sensitive UX (query refinement, tiny summaries); heavier
// synthesis still flows through `/api/ask` and the server-side Vertex
// fallback in apps/functions/src/gemini/.
export const ai = getAI(firebaseApp, { backend: new VertexAIBackend() });

if (usingEmulator) {
  // Ports default to the Firebase Emulator Suite stock values. Overridable
  // via VITE_*_EMULATOR_PORT so e2e runs can dodge collisions (Docker's
  // local proxy camps on :8080 by default and breaks the stock Firestore
  // binding).
  const authPort = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT ?? 9099);
  const firestorePort = Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT ?? 8080);
  const storagePort = Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT ?? 9199);
  connectAuthEmulator(auth, `http://127.0.0.1:${authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', firestorePort);
  connectStorageEmulator(storage, '127.0.0.1', storagePort);
}
